// ─────────────────────────────────────────────────────────────────────────────
//  Question packs — the user-authored content of a game.
//
//  A pack is plain JSON. It is created in the browser editor, saved to
//  localStorage, exported/imported as a .json file, and uploaded to a room's
//  Durable Object when the room is created. Nothing about a pack is compiled
//  in, so every room can have completely different content.
// ─────────────────────────────────────────────────────────────────────────────

export const PACK_VERSION = 1;

export interface QuestionDef {
  points: number;
  q: string;
  a: string;
  image?: string | null;
}

export interface CategoryDef {
  category: string;
  questions: QuestionDef[];
}

export interface RoundDef {
  name: string;
  categories: CategoryDef[];
}

/** Final-Jeopardy-style round: one question, everyone wagers, all revealed at once. */
export interface WagerRoundDef {
  name: string;
  question: string;
  answer: string;
  image?: string | null;
}

export interface Pack {
  version: number;
  title: string;
  rounds: RoundDef[];
  wager: WagerRoundDef | null;
}

// ───────────────────────────── limits ─────────────────────────────
// Generous for a party, bounded so a malicious pack can't exhaust a room's
// storage. The Durable Object enforces these on upload; the editor enforces
// them at authoring time so you find out before you try to host.

export const LIMITS = {
  title: 80,
  rounds: 6,
  categoriesPerRound: 8,
  questionsPerCategory: 10,
  categoryName: 60,
  questionText: 600,
  answerText: 300,
  imageUrl: 600,
  packBytes: 512 * 1024,
} as const;

const str = (v: unknown, max: number): string =>
  typeof v === "string" ? v.replace(/\s+/g, " ").trim().slice(0, max) : "";

const int = (v: unknown, min: number, max: number, fallback: number): number => {
  const n = Math.trunc(Number(v));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
};

/** Only http(s) image URLs are kept; anything else (javascript:, data:) is dropped. */
function safeImage(v: unknown): string | null {
  const s = str(v, LIMITS.imageUrl);
  if (!s) return null;
  try {
    const u = new URL(s);
    return u.protocol === "https:" || u.protocol === "http:" ? s : null;
  } catch {
    return null;
  }
}

export interface PackIssue {
  path: string;
  message: string;
}

export interface PackParseResult {
  pack: Pack;
  issues: PackIssue[];
}

/**
 * Coerce arbitrary input into a valid Pack.
 *
 * This never throws and never rejects: it repairs what it can and reports what
 * it changed. An import of a hand-edited or third-party file therefore always
 * produces something playable, and the editor can show the user what was fixed.
 * The server runs the exact same function on upload, so a client that skips
 * validation cannot inject anything the editor would have refused.
 */
export function parsePack(input: unknown): PackParseResult {
  const issues: PackIssue[] = [];
  const raw = (input ?? {}) as Record<string, unknown>;

  const title = str(raw.title, LIMITS.title) || "Jeopardy";

  const rawRounds = Array.isArray(raw.rounds) ? raw.rounds : [];
  if (rawRounds.length === 0) issues.push({ path: "rounds", message: "no rounds found" });
  if (rawRounds.length > LIMITS.rounds) {
    issues.push({ path: "rounds", message: `kept the first ${LIMITS.rounds} rounds` });
  }

  const rounds: RoundDef[] = rawRounds.slice(0, LIMITS.rounds).map((r, ri) => {
    const rr = (r ?? {}) as Record<string, unknown>;
    const rawCats = Array.isArray(rr.categories) ? rr.categories : [];
    if (rawCats.length > LIMITS.categoriesPerRound) {
      issues.push({
        path: `rounds[${ri}].categories`,
        message: `kept the first ${LIMITS.categoriesPerRound} categories`,
      });
    }

    const categories: CategoryDef[] = rawCats
      .slice(0, LIMITS.categoriesPerRound)
      .map((c, ci) => {
        const cc = (c ?? {}) as Record<string, unknown>;
        const rawQs = Array.isArray(cc.questions) ? cc.questions : [];
        if (rawQs.length > LIMITS.questionsPerCategory) {
          issues.push({
            path: `rounds[${ri}].categories[${ci}].questions`,
            message: `kept the first ${LIMITS.questionsPerCategory} questions`,
          });
        }

        // Cells are addressed by point value, so duplicates inside one category
        // would make a tile unreachable and the round unfinishable. Bump
        // collisions to the next free value rather than silently dropping them.
        const used = new Set<number>();
        const questions: QuestionDef[] = rawQs
          .slice(0, LIMITS.questionsPerCategory)
          .map((q, qi) => {
            const qq = (q ?? {}) as Record<string, unknown>;
            let points = int(qq.points, -100000, 100000, (qi + 1) * 100);
            if (used.has(points)) {
              const original = points;
              while (used.has(points)) points += 100;
              issues.push({
                path: `rounds[${ri}].categories[${ci}].questions[${qi}]`,
                message: `duplicate point value ${original} → ${points}`,
              });
            }
            used.add(points);
            return {
              points,
              q: str(qq.q, LIMITS.questionText),
              a: str(qq.a, LIMITS.answerText),
              image: safeImage(qq.image),
            };
          });

        return {
          category: str(cc.category, LIMITS.categoryName) || `Category ${ci + 1}`,
          questions,
        };
      })
      .filter((c) => c.questions.length > 0);

    return { name: str(rr.name, LIMITS.categoryName) || `Round ${ri + 1}`, categories };
  }).filter((r) => r.categories.length > 0);

  let wager: WagerRoundDef | null = null;
  if (raw.wager && typeof raw.wager === "object") {
    const w = raw.wager as Record<string, unknown>;
    const question = str(w.question, LIMITS.questionText);
    if (question) {
      wager = {
        name: str(w.name, LIMITS.categoryName) || "Final",
        question,
        answer: str(w.answer, LIMITS.answerText),
        image: safeImage(w.image),
      };
    } else {
      issues.push({ path: "wager", message: "final round has no question — dropped" });
    }
  }

  return { pack: { version: PACK_VERSION, title, rounds, wager }, issues };
}

/** A pack is playable once at least one round or the final round has content. */
export function isPlayable(pack: Pack): boolean {
  return pack.rounds.length > 0 || pack.wager !== null;
}

/** Questions with no text — the editor warns about these before you host. */
export function emptyQuestionCount(pack: Pack): number {
  let n = 0;
  for (const r of pack.rounds) for (const c of r.categories) for (const q of c.questions) {
    if (!q.q.trim()) n++;
  }
  return n;
}

export function emptyPack(title = "My Jeopardy"): Pack {
  return { version: PACK_VERSION, title, rounds: [], wager: null };
}

export function blankRound(
  name: string,
  categoryLabel: string,
  rows = 5,
  categories = 3,
  step = 100,
): RoundDef {
  return {
    name,
    categories: Array.from({ length: categories }, (_, i) => ({
      category: `${categoryLabel} ${i + 1}`,
      questions: Array.from({ length: rows }, (_, k) => ({
        points: (k + 1) * step,
        q: "",
        a: "",
        image: null,
      })),
    })),
  };
}

/** Total question slots in a pack, and how many actually have text. */
export function packProgress(pack: Pack): { written: number; total: number } {
  let written = 0;
  let total = 0;
  for (const r of pack.rounds) {
    for (const c of r.categories) {
      for (const q of c.questions) {
        total++;
        if (q.q.trim()) written++;
      }
    }
  }
  if (pack.wager) {
    total++;
    if (pack.wager.question.trim()) written++;
  }
  return { written, total };
}

/**
 * The distinct point values a round uses, in order. Cells are addressed by
 * points, so this is the row axis of the board grid.
 */
export function pointRows(round: RoundDef): number[] {
  const seen = new Set<number>();
  const rows: number[] = [];
  for (const c of round.categories) {
    for (const q of c.questions) {
      if (!seen.has(q.points)) {
        seen.add(q.points);
        rows.push(q.points);
      }
    }
  }
  return rows.sort((a, b) => a - b);
}
